<template>
  <div v-if="hasChatRight" class="chat-home-teaser mx-3 mt-4 mb-2">
    <div class="d-flex justify-space-between align-end mb-3 px-1">
      <div class="d-flex align-center">
        <h3 class="text-h6 font-weight-black white--text tracking-wide">Nachrichten</h3>
        <v-chip v-if="totalUnread > 0" x-small color="primary" class="ml-2 font-weight-bold px-2 pulse-badge"> {{ totalUnread }} NEU </v-chip>
      </div>

      <div
        @click="goToAllChats"
        class="caption grey--text text--lighten-1 font-weight-bold hover-scale d-flex align-center"
        style="cursor: pointer; transition: color 0.2s"
        v-ripple>
        Alle anzeigen
        <v-icon x-small class="ml-1" color="grey lighten-1">mdi-arrow-right</v-icon>
      </div>
    </div>

    <div v-if="isLoadingGroups && (!recentChats || recentChats.length === 0)">
      <v-skeleton-loader type="list-item-avatar-two-line" class="mb-2 rounded-xl glass-skeleton" dark></v-skeleton-loader>
      <v-skeleton-loader type="list-item-avatar-two-line" class="rounded-xl glass-skeleton" dark></v-skeleton-loader>
    </div>

    <div v-else-if="recentChats.length === 0" class="glass-panel empty-state pa-5 text-center rounded-xl">
      <v-avatar color="rgba(255,255,255,0.05)" size="50" class="mb-3">
        <v-icon color="grey lighten-1">mdi-chat-processing-outline</v-icon>
      </v-avatar>
      <div class="grey--text text--lighten-1 caption mb-3 font-weight-medium">Noch keine Nachrichten.<br />Startklar für dein erstes Gespräch?</div>
      <v-btn rounded small depressed color="primary" class="font-weight-bold px-4" @click="goToAllChats"> Chat starten </v-btn>
    </div>

    <transition-group name="staggered-slide" tag="div" v-else>
      <v-card
        v-for="(chat, index) in recentChats"
        :key="chat._id"
        @click="openChat(chat)"
        class="mb-2 glass-panel rounded-xl elevation-3 chat-card"
        :class="{ 'unread-glow': chat.hasUnread }"
        v-ripple>
        <div class="d-flex align-center pa-3">
          <div class="avatar-wrapper mr-3">
            <v-avatar size="48" class="elevation-2">
              <v-img v-if="getDisplayAvatar(chat)" :src="getDisplayAvatar(chat)" transition="scale-transition"></v-img>
              <v-sheet
                v-else
                :class="getAvatarGradient(getDisplayName(chat))"
                class="fill-height d-flex justify-center align-center gradient-avatar"
                width="100%">
                <span class="white--text font-weight-bold text-h6" style="text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3)">
                  {{ getInitials(getDisplayName(chat)) }}
                </span>
              </v-sheet>
            </v-avatar>
            <div v-if="chat.hasUnread" class="notification-dot"></div>
          </div>

          <div class="flex-grow-1" style="min-width: 0; overflow: hidden">
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-bold text-truncate white--text" style="font-size: 0.95rem; letter-spacing: 0.3px">
                {{ getDisplayName(chat) }}
              </div>
              <div class="caption time-label ml-2 flex-shrink-0">
                {{ formatTime(chat.lastMessage ? chat.lastMessage.createdAt : chat.createdAt) }}
              </div>
            </div>

            <div class="d-flex justify-space-between align-center">
              <div class="text-truncate caption message-preview" style="max-width: 90%">
                <template v-if="chat.lastMessage">
                  <span v-if="isMe(chat.lastMessage.senderId)" class="primary--text font-weight-bold" style="font-size: 0.75rem">Du: </span>
                  <span v-else-if="chat.lastMessage.senderId" class="grey--text text--lighten-2 font-weight-bold" style="font-size: 0.75rem">
                    {{ chat.lastMessage.senderId.vorname }}:
                  </span>

                  <span :class="chat.hasUnread ? 'white--text font-weight-medium' : 'grey--text text--lighten-1'">
                    {{ getPreviewText(chat.lastMessage) }}
                  </span>
                </template>
                <template v-else>
                  <span class="grey--text font-italic">Tippe um zu schreiben... 👋</span>
                </template>
              </div>

              <v-icon v-if="!chat.hasUnread" x-small color="grey darken-2">mdi-chevron-right</v-icon>
            </div>
          </div>
        </div>
      </v-card>
    </transition-group>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex"
import userService from "@/services/userService"

export default {
  name: "ChatHomeTeaser",
  computed: {
    ...mapState("chatStore", ["chatGroups", "isLoadingGroups"]),
    ...mapState("userStore", ["user"]),

    hasChatRight() {
      return userService.hasRight("chat_view")
    },

    totalUnread() {
      if (!this.chatGroups) return 0
      return this.chatGroups.filter((g) => g.hasUnread).length
    },

    recentChats() {
      if (!this.chatGroups || !this.chatGroups.length) return []

      const sorted = [...this.chatGroups].sort((a, b) => {
        const dateA = new Date(a.lastMessage ? a.lastMessage.createdAt : a.createdAt)
        const dateB = new Date(b.lastMessage ? b.lastMessage.createdAt : b.createdAt)
        return dateB - dateA
      })

      return sorted.slice(0, 2)
    },
  },
  methods: {
    ...mapActions("chatStore", ["fetchGroups", "setActiveGroup"]),
    handleAppResume() {
      if (document.visibilityState === "visible") {
        console.log("App ist zurück im Vordergrund (Teaser) – Lade Gruppen neu...")
        this.fetchGroups()
      }
    },
    goToAllChats() {
      this.$router.push("/chat")
    },

    openChat(group) {
      this.setActiveGroup(group._id)
      this.$router.push("/chat")
    },

    isMe(senderId) {
      if (!this.user || !senderId) return false
      return (senderId._id || senderId) === this.user._id
    },

    getInitials(name) {
      if (!name) return "CH"
      const parts = name.trim().split(" ")
      if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name.substring(0, 2).toUpperCase()
    },

    // Generiert einen deterministischen Gradienten basierend auf dem Namen
    getAvatarGradient(name) {
      const gradients = ["bg-gradient-1", "bg-gradient-2", "bg-gradient-3", "bg-gradient-4"]
      if (!name) return gradients[0]
      const charCode = name.charCodeAt(0) + (name.length > 1 ? name.charCodeAt(1) : 0)
      return gradients[charCode % gradients.length]
    },

    getOtherMember(group) {
      if (!group || !group.members || !this.user) return null
      return group.members.find((m) => m.userId && (m.userId._id || m.userId) !== this.user._id)
    },
    getDisplayAvatar(group) {
      if (group?.isDirect) {
        const other = this.getOtherMember(group)
        return other && other.userId && other.userId.bild ? "/img/profilbilder/" + other.userId.bild : null
      }
      return group?.bild ? "/img/profilbilder/" + group.bild : null
    },
    getDisplayName(group) {
      if (group?.isDirect) {
        const other = this.getOtherMember(group)
        return other && other.userId ? `${other.userId.vorname} ${other.userId.nachname}` : "Gelöschter Nutzer"
      }
      return group?.name || "Gruppe"
    },

    getPreviewText(message) {
      if (message.textContent) return message.textContent
      if (message.messageType === "image") return "📷 Foto"
      if (message.messageType === "video") return "🎥 Video"
      if (message.messageType === "audio") return "🎤 Sprachnachricht"
      if (message.fileInfo) return "📎 Datei"
      return "..."
    },

    formatTime(timestamp) {
      if (!timestamp) return ""
      const date = this.$moment(timestamp)
      if (date.isSame(this.$moment(), "day")) return date.format("HH:mm")
      if (date.isSame(this.$moment().subtract(1, "days"), "day")) return "Gestern"
      return date.format("DD.MM")
    },
  },
  mounted() {
    if (this.hasChatRight && (!this.chatGroups || this.chatGroups.length === 0)) {
      this.fetchGroups()
    }

    // NEU: Listener hinzufügen
    document.addEventListener("visibilitychange", this.handleAppResume)
    document.addEventListener("resume", this.handleAppResume) // Für Cordova/Capacitor
  },
  beforeDestroy() {
    // NEU: Aufräumen
    document.removeEventListener("visibilitychange", this.handleAppResume)
    document.removeEventListener("resume", this.handleAppResume)
  },
}
</script>

<style scoped lang="scss">
/* --- 1. GLASS PANEL UPGRADE --- */
.glass-panel {
  /* Mehr Tiefe durch semi-transparentes Schwarz + Blur */
  background: rgba(30, 30, 30, 0.6) !important;
  backdrop-filter: blur(16px) saturate(140%);
  -webkit-backdrop-filter: blur(16px) saturate(140%);

  /* Feiner Rand (Inset Border Look) */
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);

  transition: all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;

  /* Subtiler Schein von oben */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  }
}

.glass-panel:active {
  transform: scale(0.98);
  background: rgba(40, 40, 40, 0.7) !important;
}

/* --- 2. CARD HOVER & GLOW --- */
.unread-glow {
  border: 1px solid rgba(7, 108, 9, 0.3) !important;
  box-shadow: 0 0 15px rgba(0, 230, 118, 0.1), inset 0 0 20px rgba(0, 230, 118, 0.05);
}

.chat-card:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
}

/* --- 3. AVATAR STYLING --- */
.avatar-wrapper {
  position: relative;
}

.notification-dot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 14px;
  height: 14px;
  background-color: var(--v-primary);
  border: 1px solid white; /* Passt sich dem Hintergrund an */
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 230, 118, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(0, 230, 118, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 230, 118, 0);
  }
}

/* Coole Gradients für User ohne Bild */
.bg-gradient-1 {
  background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%);
}
.bg-gradient-2 {
  background: linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%);
}
.bg-gradient-3 {
  background: linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%);
}
.bg-gradient-4 {
  background: linear-gradient(135deg, #fccb90 0%, #d57eeb 100%);
}

/* --- 4. TYPOGRAPHY & UTILS --- */
.tracking-wide {
  letter-spacing: 0.5px;
}

.time-label {
  font-family: "Roboto Mono", monospace; /* Monospace für Zahlen sieht oft technisch/cool aus */
  font-size: 0.7rem !important;
  color: rgba(255, 255, 255, 0.4);
}

.hover-scale:hover {
  transform: translateX(4px);
  color: white !important;
}

.pulse-badge {
  animation: pulse-opacity 2s infinite ease-in-out;
}
@keyframes pulse-opacity {
  0% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.8;
  }
}

/* --- 5. ANIMATIONS --- */
.staggered-slide-enter-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.staggered-slide-enter {
  opacity: 0;
  transform: translateY(20px);
}
/* Staggering (Verzögerung) via CSS nth-child wäre möglich, 
   aber für 2 Items reicht der Standard-Slide */

.glass-skeleton {
  background: rgba(255, 255, 255, 0.03) !important;
}
</style>
